import { createMarkup } from '@packages/domain/utils/createMarkup/createMarkup';
import { Spinner } from 'components/Spinner/Spinner';
import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import type { RootState } from 'reducers/types';
import { isFetching } from 'selectors/ui/ui';
import { useTranslations } from 'utils/translations/useTranslations';

import './Fetching.scss';
import { FIVE_SECONDS_IN_MS } from 'constants/timers';

type FetchingComponentProps = {
  isFetching: boolean;
  children?: ReactNode | ((params: { isFetching: boolean }) => ReactNode);
};

const IsFetching = ({
  children = null,
  isFetching,
}: FetchingComponentProps) => {
  const [isStillFetching, setIsStillFetching] = useState(false);
  const countdown = useRef<number | null>(null);
  const i18n = useTranslations();

  useEffect(() => {
    if (!isFetching) {
      isStillFetching && setIsStillFetching(false);

      return;
    }

    countdown.current = window.setTimeout(() => {
      setIsStillFetching(true);
    }, FIVE_SECONDS_IN_MS);

    return () => {
      if (countdown.current !== null) {
        clearTimeout(countdown.current);
      }
    };
  }, [isFetching, isStillFetching]);

  return (
    <>
      {isFetching && (
        <div className="Fetching">
          <Spinner role="status" data-testid="spinner">
            <span
              className={[isStillFetching ? '' : 'hidden']
                .filter(Boolean)
                .join(' ')}
              data-hidden={!isStillFetching}
              data-testid="isStillFetching"
              dangerouslySetInnerHTML={createMarkup(
                i18n.t('fetching.isStillFetching'),
              )}
            />
          </Spinner>
        </div>
      )}
      {typeof children === 'function' ? children({ isFetching }) : children}
    </>
  );
};

const mapStateToProps = (state: RootState, props: { entity: string }) => {
  const { entity } = props;

  return {
    isFetching: isFetching(state, entity),
  };
};

const connector = connect(mapStateToProps);

const Fetching = connector(IsFetching);

export { Fetching, IsFetching };
